import { Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import BestPractisesButton from "../../../../components/common/BestPractisesButton/BestPractisesButton.tsx";
import AdvancedImageLoading from "../../../../components/TailoredGeneration/SharedPanels/AdvancedImageLoading/AdvancedImageLoading";
import AdvancedImageLoadingCompleted from "../../../../components/TailoredGeneration/SharedPanels/AdvancedImageLoadingCompleted/AdvancedImageLoadingCompleted";
import AdvancedImageLoadingError from "../../../../components/TailoredGeneration/SharedPanels/AdvancedImageLoadingError/AdvancedImageLoadingError";
import SelectTgUploadMethod from "../../../../components/TailoredGeneration/SharedPanels/SelectTgUploadMethod/SelectTgUploadMethod.tsx";
import TgAdvancedImageUploadSetup from "../../../../components/TailoredGeneration/SharedPanels/TgAdvancedImageUploadSetup/TgAdvancedImageUploadSetup.tsx";
import RouterConstants from "../../../../constants/RouterConstants.ts";
import useSecureNavigate from "../../../../hooks/useSecureNavigate.tsx";
import { useAppStore } from "../../../../hooks/useStores";
import EditorLayout from "../../../../layout/EditorLayout/EditorLayout.tsx";
import { isLegacyTgRoute } from "../../utils/tg-utils.ts";
import { BulkUploadStatus, defaultDataset } from "../store/tg-dataset-store.tsx";
import styles from "./CreateTgDataset.module.scss";

enum CreateTgDatasetPanels {
	UPLOAD_METHOD_PANEL = "UPLOAD_METHOD_PANEL",
	ADVANCED_UPLOAD_PANEL = "ADVANCED_UPLOAD_PANEL",
	ADVANCED_UPLOAD_LOADING_PANEL = "ADVANCED_UPLOAD_LOADING_PANEL",
	ADVANCED_UPLOAD_COMPLETED_PANEL = "ADVANCED_UPLOAD_COMPLETED_PANEL",
	ADVANCED_UPLOAD_ERROR_PANEL = "ADVANCED_UPLOAD_ERROR_PANEL",
}

const CreateTgDatasetPage = () => {
	const { t } = useTranslation("tg", { keyPrefix: "datasets.create" });
	const { tgStore } = useAppStore();
	const { tgModelStore, tgDatasetStore } = tgStore;
	const [currentPanel, setCurrentPanel] = useState<CreateTgDatasetPanels>(CreateTgDatasetPanels.UPLOAD_METHOD_PANEL);
	const [initialStatus, setInitialStatus] = useState<BulkUploadStatus | null>(null);
	const [lastStatus, setLastStatus] = useState<BulkUploadStatus | null>(null);
	const navigate = useSecureNavigate();
	const { projectId } = useParams();

	const stillUploadingImages = tgDatasetStore.datasetForm.images.filter((img) => img.uploading);
	const uploadedImages = tgDatasetStore.datasetForm.images.filter((img) => !img.uploading && !img.error);
	const loadingDatasetSave =
		tgDatasetStore.loadingUpdateDataset ||
		tgDatasetStore.loadingDuplicateDataset ||
		tgDatasetStore.loadingUploadImages ||
		tgDatasetStore.loadingRegenerate;

	useEffect(() => {
		return () => {
			tgModelStore.isDatasetFromExisting = false;
			tgDatasetStore.datasetForm = defaultDataset;
			window.history.replaceState({}, "");
		};
	}, []);

	const handleUseAdvancedNext = () => {
		setCurrentPanel(CreateTgDatasetPanels.ADVANCED_UPLOAD_PANEL);
	};

	const handleCancelUpload = () => {
		tgDatasetStore.datasetForm.id = NaN;
		setCurrentPanel(CreateTgDatasetPanels.ADVANCED_UPLOAD_PANEL);
		setInitialStatus(null);
	};

	const handleLoadingCompleted = (status: BulkUploadStatus) => {
		setLastStatus(status);
		setCurrentPanel(CreateTgDatasetPanels.ADVANCED_UPLOAD_COMPLETED_PANEL);
	};

	const handleAdvancedImageUploadComplete = (firstStatus: BulkUploadStatus) => {
		setInitialStatus(firstStatus);
		setCurrentPanel(CreateTgDatasetPanels.ADVANCED_UPLOAD_LOADING_PANEL);
	};

	const handleAdvancedLoadingCompletedContinue = () => {
		const base = isLegacyTgRoute()
			? RouterConstants.NEW_TG.LEGACY.PROJECTS.fullPath
			: RouterConstants.NEW_TG.PROJECTS.fullPath;
		navigate(`${base}/${projectId}/${RouterConstants.NEW_TG.DATASETS.path}`);
	};

	const renderCurrentPanel = () => {
		switch (currentPanel) {
			case CreateTgDatasetPanels.UPLOAD_METHOD_PANEL:
				return <SelectTgUploadMethod fromDatasetPage={true} handleUseAdvancedNext={handleUseAdvancedNext} />;
			case CreateTgDatasetPanels.ADVANCED_UPLOAD_PANEL:
				return <TgAdvancedImageUploadSetup onComplete={handleAdvancedImageUploadComplete} />;
			case CreateTgDatasetPanels.ADVANCED_UPLOAD_LOADING_PANEL:
				return (
					<AdvancedImageLoading
						datasetId={tgDatasetStore.datasetForm.id}
						onCancel={handleCancelUpload}
						onCompleted={handleLoadingCompleted}
						onError={() => setCurrentPanel(CreateTgDatasetPanels.ADVANCED_UPLOAD_ERROR_PANEL)}
						initialStatus={initialStatus ?? undefined}
					/>
				);
			case CreateTgDatasetPanels.ADVANCED_UPLOAD_COMPLETED_PANEL:
				return lastStatus ? (
					<AdvancedImageLoadingCompleted
						handleContinueClick={handleAdvancedLoadingCompletedContinue}
						status={lastStatus}
						isFromCreateDataset
					/>
				) : null;
			case CreateTgDatasetPanels.ADVANCED_UPLOAD_ERROR_PANEL:
				return (
					<AdvancedImageLoadingError
						onTryAgain={() => setCurrentPanel(CreateTgDatasetPanels.ADVANCED_UPLOAD_PANEL)}
					/>
				);
			default:
				return <SelectTgUploadMethod fromDatasetPage={true} handleUseAdvancedNext={handleUseAdvancedNext} />;
		}
	};

	return (
		<EditorLayout
			childrenClassName={styles.wrapper}
			name={t("title")}
			loadingSave={loadingDatasetSave}
			progress={(uploadedImages.length / tgDatasetStore.datasetForm.images.length) * 100}
			primaryButton={
				stillUploadingImages.length ? (
					<>
						<Typography className={styles.uploadLoader}>{t("uploadLoader")}</Typography>
						<Typography className={styles.uploadLoaderCount}>
							{`${uploadedImages.length}/${tgDatasetStore.datasetForm.images.length}`}
						</Typography>
					</>
				) : null
			}
			secondaryButton={stillUploadingImages.length ? null : <BestPractisesButton fontSize="small" />}
		>
			{renderCurrentPanel()}
		</EditorLayout>
	);
};

export default observer(CreateTgDatasetPage);
